import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Receipt, CheckCircle2, XCircle, Download } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUSES = ["", "PENDING_PAYMENT", "PENDING_APPROVAL", "ACTIVE", "REJECTED"];
const BADGES = {
  PENDING_PAYMENT: "bg-slate-500",
  PENDING_APPROVAL: "bg-amber-500",
  ACTIVE: "bg-emerald-600",
  REJECTED: "bg-red-500",
};

const PORTAL_TONES = {
  ORGANIZATION: "bg-emerald-100 text-emerald-900 border-emerald-300",
  VENDOR: "bg-pink-100 text-pink-900 border-pink-300",
  DISTRIBUTOR: "bg-blue-100 text-blue-900 border-blue-300",
};

export default function PlatformSubscriptions() {
  const [subs, setSubs] = useState([]);
  const [statusFilter, setStatusFilter] = useState("__ALL__");
  const [rejectOpen, setRejectOpen] = useState(null);
  const [reason, setReason] = useState("");

  const load = async () => {
    const url = statusFilter && statusFilter !== "__ALL__" ? `/platform/subscriptions?status=${statusFilter}` : "/platform/subscriptions";
    const { data } = await api.get(url);
    setSubs(data);
  };
  useEffect(() => { load(); }, [statusFilter]);

  const approve = async (s) => {
    if (!window.confirm(`Approve ${s.company_name} on ${s.plan_code}? This will generate the contract PDF and notify the customer.`)) return;
    try { await api.post(`/platform/subscriptions/${s.id}/approve`); toast.success("Approved — contract generated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const reject = async () => {
    try { await api.post(`/platform/subscriptions/${rejectOpen.id}/reject`, { reason: reason || "Not approved" }); toast.success("Rejected"); setRejectOpen(null); setReason(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="admin-platform-subscriptions">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black flex items-center gap-2"><Receipt className="w-7 h-7 text-primary" /> Subscriptions</h1>
          <p className="text-muted-foreground">Paid subscriptions waiting for activation. Approve to generate contract & notify customer.</p>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48" data-testid="subs-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__ALL__">All statuses</SelectItem>
            {STATUSES.filter(Boolean).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Company</TableHead><TableHead>Contact</TableHead><TableHead>Portal</TableHead>
            <TableHead>Plan</TableHead><TableHead className="text-right">Total</TableHead>
            <TableHead>Status</TableHead><TableHead>Created</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {subs.map((s) => (
              <TableRow key={s.id} data-testid={`sub-row-${s.id}`}>
                <TableCell><div className="font-medium">{s.company_name}</div><div className="text-xs text-muted-foreground">{s.email}</div></TableCell>
                <TableCell>{s.contact_name || "—"}<div className="text-xs text-muted-foreground">{s.phone}</div></TableCell>
                <TableCell><Badge variant="outline" className={`font-mono text-[10px] ${PORTAL_TONES[s.portal] || ""}`} data-testid={`sub-portal-${s.id}`}>{s.portal || "—"}</Badge></TableCell>
                <TableCell><Badge>{s.plan_code} · {s.billing_cycle}</Badge></TableCell>
                <TableCell className="text-right font-mono">₹{(s.total_price || 0).toLocaleString()}</TableCell>
                <TableCell><Badge className={BADGES[s.status]}>{s.status}</Badge></TableCell>
                <TableCell className="font-mono text-xs">{(s.created_at || "").slice(0, 10)}</TableCell>
                <TableCell className="text-right space-x-1">
                  {s.status === "PENDING_APPROVAL" ? (
                    <>
                      <Button size="sm" variant="outline" onClick={() => approve(s)} data-testid={`sub-approve-${s.id}`}>
                        <CheckCircle2 className="w-4 h-4 mr-1 text-emerald-500" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { setRejectOpen(s); setReason(""); }} data-testid={`sub-reject-${s.id}`}>
                        <XCircle className="w-4 h-4 text-red-500" />
                      </Button>
                    </>
                  ) : null}
                  {s.status === "ACTIVE" && s.contract_pdf_path ? (
                    <a href={`${BACKEND}/api/platform/public/subscriptions/${s.id}`} target="_blank" rel="noreferrer">
                      <Button size="sm" variant="outline" data-testid={`sub-view-${s.id}`}><Download className="w-3 h-3" /></Button>
                    </a>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
            {subs.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-10">No subscriptions for this filter.</TableCell></TableRow> : null}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!rejectOpen} onOpenChange={(v) => !v && setRejectOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reject subscription · {rejectOpen?.company_name}</DialogTitle></DialogHeader>
          <Textarea placeholder="Reason for rejection (visible to customer if email reaches them)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="sub-reject-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(null)}>Cancel</Button>
            <Button onClick={reject} data-testid="sub-reject-confirm">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
